/**
 * Copyright (c) Microsoft. All rights reserved.
 */
import { IComponentStyles } from '@fluentui/foundation-legacy';
import { IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { IStackProps, IStackSlots, Stack } from '@fluentui/react/lib/Stack';
import { ITextProps, ITextSlots } from '@fluentui/react/lib/Text';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import React from 'react';
import { defineMessages } from 'react-intl';
import { EmptyStateView } from 'src/core/components/EmptyStateView';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { EntityStore } from 'src/core/types/EntityStore';
import { CustomTextClassStore } from 'src/customText/core/stores/CustomTextClassStore';
import { CustomTextDocumentMetadata } from 'src/customText/core/stores/CustomTextDocumentStore';
import { CustomTextEntityLabelStore } from 'src/customText/core/stores/CustomTextEntityLabelStore';
import { CustomTextProjectKind } from 'src/customText/core/stores/CustomTextProjectStore';
import { DistributionBar } from 'src/modules/dataDistribution/DistributionBar';

type CustomTextDataDistributionProps = {
    projectKind: CustomTextProjectKind;
    documents: CustomTextDocumentMetadata[];
    entities?: EntityStore[];
    classes?: CustomTextClassStore[];
    isInEmptyState: boolean;
};

type DataDistribution = {
    name: string;
    testingCount: number;
    trainingCount: number;
    testingPercentage: number;
    trainingPercentage: number;
};

type DistributionDisplay = 'acrossDocuments' | 'acrossTags';

const messages = defineMessages({
    testingDataset: { id: 'customText.customTextDataDistribution.testingDataset', defaultMessage: 'Testing dataset ({count})' },
    trainingDataset: { id: 'customText.customTextDataDistribution.trainingDataset', defaultMessage: 'Training dataset ({count})' },
    viewTaggedEntities: {
        id: 'customText.customTextDataDistribution.viewTaggedEntities',
        defaultMessage: 'View labeled entities by'
    },
    distributionOfClasses: {
        id: 'customText.customTextDataDistribution.distributionOfClasses',
        defaultMessage: 'Distribution of classes'
    },
    distributionAcrossFiles: {
        id: 'customText.customTextDataDistribution.distributionAcrossFiles',
        defaultMessage: 'Documents with at least one label'
    },
    distributionAcrossTags: {
        id: 'customText.customTextDataDistribution.distributionAcrossTags',
        defaultMessage: 'Total instances'
    },
    emptyStateMsg: {
        id: 'customText.customTextDataDistribution.emptyStateMsg',
        defaultMessage: 'No labels to show yet. Begin labeling to see how your labels are distributed.'
    }
});

const theme = getScopedTheme('CustomTextDataDistribution');

const textStyles = classNamesFunction<ITextProps, IComponentStyles<ITextSlots>>()({ root: { display: 'flex', textAlign: 'center' } });

const legendColors: { [key in DistributionDisplay]: { training: string; testing: string } } = {
    acrossDocuments: { training: theme.trainingAcrossDocumentsColor, testing: theme.testingAcrossDocumentsColor },
    acrossTags: { training: theme.trainingAcrossTagsColor, testing: theme.testingAcrossTagsColor }
};

const getLegendIcon = (color: string) => <div style={{ background: color, width: '10px', height: '10px' }} />;

const containerStackStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
    root: { minHeight: 0, paddingBottom: '30px' }
});
const dataWrapperStackStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({ root: { overflowY: 'auto' } });

/**
 * A component rendering the data distribution tabs in both extraction and classification tagging palettes.
 */
export const CustomTextDataDistribution = (props: CustomTextDataDistributionProps) => {
    const { projectKind, documents, entities = [], classes = [], isInEmptyState } = props;

    const { localize } = useLocalization();
    const [selectedDistributionDisplay, setSelectedDistributionDisplay] = React.useState<DistributionDisplay>('acrossTags');

    const dataDistributionOptions: IDropdownOption[] = [
        { key: 'acrossDocuments', text: localize(messages.distributionAcrossFiles) },
        { key: 'acrossTags', text: localize(messages.distributionAcrossTags) }
    ];

    const testingSetDocuments = documents.filter(d => d.documentDataset === 'Test');
    const trainingSetDocuments = documents.filter(d => d.documentDataset === 'Train');
    const testingSetTags =
        projectKind === 'CustomEntityRecognition' || projectKind === 'CustomHealthcare'
            ? testingSetDocuments.reduce((acc, curr) => [...acc, ...curr.entityLabels], [])
            : testingSetDocuments.reduce((acc, curr) => [...acc, ...curr.classLabels], []);
    const trainingSetTags =
        projectKind === 'CustomEntityRecognition' || projectKind === 'CustomHealthcare'
            ? trainingSetDocuments.reduce((acc, curr) => [...acc, ...curr.entityLabels], [])
            : trainingSetDocuments.reduce((acc, curr) => [...acc, ...curr.classLabels], []);

    let testingCountToDisplay = 0;
    let trainingCountToDisplay = 0;

    switch (selectedDistributionDisplay) {
        case 'acrossDocuments':
            testingCountToDisplay = testingSetDocuments.length;
            trainingCountToDisplay = trainingSetDocuments.length;
            break;
        case 'acrossTags':
        default:
            testingCountToDisplay = testingSetTags.length;
            trainingCountToDisplay = trainingSetTags.length;
    }

    const calculateClassificationSetPercentage = (set: CustomTextDocumentMetadata[], itemId: string) =>
        +((set.filter(d => d.classLabels.find(cl => cl.id === itemId)).length / set.length) * 100).toFixed() || 0;

    const calculateExtractionDocumentsSetPercentage = (set: CustomTextDocumentMetadata[], itemId: string) =>
        +((set.filter(d => d.entityLabels.find(cl => cl.entityId === itemId)).length / set.length) * 100).toFixed() || 0;

    const calculateExtractionTagsSetPercentage = (set: CustomTextEntityLabelStore[], itemId: string) =>
        +((set.filter(d => d.entityId === itemId).length / set.length) * 100).toFixed() || 0;

    const distributedClassesAcrossDocuments: DataDistribution[] = classes.map(c => ({
        name: c.name,
        testingCount: testingSetDocuments.filter(d => d.classLabels.find(cl => cl.id === c.id)).length,
        trainingCount: trainingSetDocuments.filter(d => d.classLabels.find(cl => cl.id === c.id)).length,
        testingPercentage: calculateClassificationSetPercentage(testingSetDocuments, c.id),
        trainingPercentage: calculateClassificationSetPercentage(trainingSetDocuments, c.id)
    }));

    const distributedEntitiesAcrossDocuments: DataDistribution[] = entities.map(entity => ({
        name: entity.name,
        testingCount: testingSetDocuments.filter(d => d.entityLabels.find(cl => cl.entityId === entity.id)).length,
        trainingCount: trainingSetDocuments.filter(d => d.entityLabels.find(cl => cl.entityId === entity.id)).length,
        testingPercentage: calculateExtractionDocumentsSetPercentage(testingSetDocuments, entity.id),
        trainingPercentage: calculateExtractionDocumentsSetPercentage(trainingSetDocuments, entity.id)
    }));

    const distributedEntitiesAcrossTags: DataDistribution[] = entities.map(entity => ({
        name: entity.name,
        testingCount: testingSetTags.filter(d => d.entityId === entity.id).length,
        trainingCount: trainingSetTags.filter(d => d.entityId === entity.id).length,
        testingPercentage: calculateExtractionTagsSetPercentage(testingSetTags, entity.id),
        trainingPercentage: calculateExtractionTagsSetPercentage(trainingSetTags, entity.id)
    }));

    let displayedDistribution: DataDistribution[] = [];
    if (projectKind === 'CustomEntityRecognition' || projectKind === 'CustomHealthcare') {
        switch (selectedDistributionDisplay) {
            case 'acrossTags':
                displayedDistribution = distributedEntitiesAcrossTags;
                break;
            case 'acrossDocuments':
            default:
                displayedDistribution = distributedEntitiesAcrossDocuments;
        }
    } else {
        displayedDistribution = distributedClassesAcrossDocuments;
    }

    return isInEmptyState ? (
        <EmptyStateView
            addBackground={false}
            imageStyles={{ image: { width: '150px' } }}
            message={<LuisText styles={textStyles}>{localize(messages.emptyStateMsg)}</LuisText>}
        />
    ) : (
        <Stack styles={containerStackStyles}>
            <Stack tokens={{ padding: 16, childrenGap: 20 }}>
                {projectKind === 'CustomEntityRecognition' || projectKind === 'CustomHealthcare' ? (
                    <LuisDropdown
                        label={localize(messages.viewTaggedEntities)}
                        options={dataDistributionOptions}
                        selectedKey={selectedDistributionDisplay}
                        onChange={(_, option) => setSelectedDistributionDisplay(option.key as DistributionDisplay)}
                    />
                ) : (
                    <LuisText fontWeight="bold">{localize(messages.distributionOfClasses)}</LuisText>
                )}

                <Stack tokens={{ childrenGap: 16 }}>
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
                        {getLegendIcon(legendColors[selectedDistributionDisplay].training)}
                        <LuisText>{localize(messages.trainingDataset, { count: trainingCountToDisplay })}</LuisText>
                    </Stack>
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
                        {getLegendIcon(legendColors[selectedDistributionDisplay].testing)}
                        <LuisText>{localize(messages.testingDataset, { count: testingCountToDisplay })}</LuisText>
                    </Stack>
                </Stack>
            </Stack>
            <Stack tokens={{ padding: 16, childrenGap: 16 }} styles={dataWrapperStackStyles}>
                {displayedDistribution.map((distribution, index) => (
                    <DistributionBar
                        text={distribution.name}
                        testingCount={distribution.testingCount}
                        trainingCount={distribution.trainingCount}
                        testingPercent={distribution.testingPercentage}
                        trainingPercent={distribution.trainingPercentage}
                        displaySeparator={index !== displayedDistribution.length - 1}
                        testingColor={legendColors[selectedDistributionDisplay].testing}
                        trainingColor={legendColors[selectedDistributionDisplay].training}
                    />
                ))}
            </Stack>
        </Stack>
    );
};
